import { useInfiniteQuery } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryKeys } from './query-client';
import type { WebWin, WinsPageDto } from './wins';

async function fetchWinsPage(cursor?: string): Promise<WinsPageDto> {
  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const res = await apiFetch(`${API_BASE}/api/wins${query}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as WinsPageDto;
}

/**
 * `enabled` is for a caller that only sometimes needs the list — the Black Friday band asks "did any win
 * land in the free window", and only once the window has closed. Defaulted true, so the Wins page and
 * every other reader are untouched; passing false shares the same cache entry without fetching, so the
 * answer arrives for free if the page has already been opened.
 *
 * `retry` overrides the app-wide `retry: false` for that caller: the band has no retry button, so a
 * single failed read would otherwise stand for the whole visit.
 */
export function useWins(enabled = true, retry: number | false = false): {
  wins: WebWin[];
  isLoading: boolean;
  isError: boolean;
  /** The last read failed, even if an earlier one left data on screen. */
  lastReadFailed: boolean;
  refetch: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
} {
  const query = useInfiniteQuery({
    queryKey: queryKeys.wins,
    queryFn: ({ pageParam }: { pageParam?: string }) => fetchWinsPage(pageParam),
    getNextPageParam: (last: WinsPageDto) => last.nextCursor,
    enabled,
    retry,
  });
  const seen = new Set<string>();
  const wins = (query.data?.pages ?? []).flatMap((p) => p.wins).filter((w) => (seen.has(w.id) ? false : (seen.add(w.id), true)));
  return {
    wins,
    isLoading: query.isLoading,
    isError: query.isError && !query.data,
    lastReadFailed: query.isError,
    refetch: () => void query.refetch(),
    hasMore: !!query.hasNextPage,
    loadingMore: query.isFetchingNextPage,
    loadMore: () => void query.fetchNextPage(),
  };
}
